import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  AttachMoney,
  Undo,
  Delete,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import ConsignmentStatusChip from '../components/ConsignmentStatusChip';
import ConsignmentForm from '../components/ConsignmentForm';

const ConsignmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consignment, setConsignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchConsignment();
  }, [id]);

  const fetchConsignment = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/consignments/${id}`);
      setConsignment(response.data);
    } catch (error) {
      console.error('Error fetching consignment:', error);
      showSnackbar('Error loading consignment details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = async () => {
    try {
      await api.put(`/api/consignments/${id}/return`, {
        return_date: new Date().toISOString().split('T')[0],
        return_reason: returnReason,
      });
      showSnackbar('Item marked as returned', 'success');
      setReturnDialogOpen(false);
      fetchConsignment();
    } catch (error) {
      console.error('Error marking as returned:', error);
      showSnackbar('Error marking item as returned', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!consignment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Consignment not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/consignments')}>
            Back
          </Button>
          <Typography variant="h4">Consignment Details</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {consignment.status === 'pending' && (
            <Button
              variant="outlined"
              startIcon={<Undo />}
              onClick={() => setReturnDialogOpen(true)}
            >
              Mark as Returned
            </Button>
          )}
          <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditOpen(true)}>
            Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Consignment Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Consignment Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <ConsignmentStatusChip status={consignment.status} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payout Status
                  </Typography>
                  <Chip
                    label={consignment.payout_status}
                    size="small"
                    color={
                      consignment.payout_status === 'paid'
                        ? 'success'
                        : consignment.payout_status === 'calculated'
                        ? 'warning'
                        : 'default'
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Consignment Date
                  </Typography>
                  <Typography variant="body1">
                    {consignment.consignment_date
                      ? new Date(consignment.consignment_date).toLocaleDateString()
                      : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Agreed Price
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    ${parseFloat(consignment.agreed_price || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Commission Rate
                  </Typography>
                  <Typography variant="body1">{consignment.commission_rate}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payout Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    ${parseFloat(consignment.payout_amount || 0).toFixed(2)}
                  </Typography>
                </Grid>
                {consignment.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">{consignment.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Vendor Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Vendor Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">
                    {consignment.vendor?.name || 'Unknown Vendor'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{consignment.vendor?.email || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{consignment.vendor?.phone || '-'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Item Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Item Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Item Name
                  </Typography>
                  <Typography variant="body1">
                    {consignment.item?.name || 'Unknown Item'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    SKU
                  </Typography>
                  <Typography variant="body1">{consignment.item?.sku || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {consignment.item?.category_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Item Status
                  </Typography>
                  <Chip
                    label={consignment.item?.status || 'unknown'}
                    size="small"
                    color={consignment.item?.status === 'sold' ? 'success' : 'default'}
                  />
                </Grid>
                {consignment.item?.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">{consignment.item.description}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sale Information (if sold) */}
        {consignment.status === 'sold' && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sale Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Sale Date
                    </Typography>
                    <Typography variant="body1">
                      {consignment.sale_date
                        ? new Date(consignment.sale_date).toLocaleDateString()
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Actual Sale Price
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ${parseFloat(consignment.actual_sale_price || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Payout Date
                    </Typography>
                    <Typography variant="body1">
                      {consignment.payout_date
                        ? new Date(consignment.payout_date).toLocaleDateString()
                        : 'Not paid yet'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Return Information (if returned) */}
        {consignment.status === 'returned' && consignment.return_date && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Return Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Return Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(consignment.return_date).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  {consignment.return_reason && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Return Reason
                      </Typography>
                      <Typography variant="body1">{consignment.return_reason}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Edit Dialog */}
      {editOpen && (
        <ConsignmentForm
          open={editOpen}
          consignment={consignment}
          onClose={() => setEditOpen(false)}
          onSuccess={() => {
            setEditOpen(false);
            fetchConsignment();
            showSnackbar('Consignment updated successfully', 'success');
          }}
        />
      )}

      {/* Return Dialog */}
      <Dialog open={returnDialogOpen} onClose={() => setReturnDialogOpen(false)}>
        <DialogTitle>Mark Item as Returned</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Return Reason (optional)"
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReturn} variant="contained" color="primary">
            Mark as Returned
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsignmentDetail;
